import { BffApiClientMolecule } from "@/app/api-client/store"
import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import { BrandSlideTitle } from "@/app/components/brands/brand-slide-title"
import {
  type BrandSliderConfig,
  isBrandSliderPreviewVideo,
} from "@/app/components/brands/brand-slider-config"
import { SliderMolecule } from "@/app/components/slider/slider"
import { DropScopeProvider } from "@/app/drop/store/scope-provider"
import type { SpotlightDrop } from "@/app/page-main.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { LaunchedCollectionPublic } from "@anaconda/core/collection-launcher/types"
import { DropPreviewType } from "@anaconda/core/drop/types"
import { atomizeDrop } from "@anaconda/core/drop/utils"
import {
  type HomepageLayoutEntry,
  applyHomepageLayout,
} from "@anaconda/core/homepage-layout/layout"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import {
  action,
  atom,
  computed,
  effect,
  withAsyncData,
  withInit,
  wrap,
} from "@reatom/core"
import { molecule } from "bunshi"
import type { ReactNode } from "react"
import {
  getCarouselSlideKey,
  getDefaultCarouselSlides,
} from "./carousel-slides"
import { DropInfo, DropTitle } from "./drops-spotlight-slide-components"

interface DropEnrichment {
  minted: number
  myMints: number
}

export interface DropSlideData {
  type: "drop"
  drop: SpotlightDrop
  dropAtom: ReturnType<typeof atomizeDrop>
  slideIndex: number
}

export interface BrandSlideData {
  type: "brand"
  slider: BrandSliderConfig
  slideIndex: number
}

export type SlideData = DropSlideData | BrandSlideData

/**
 * Module-level storage for server data
 * Populated by initializeSpotlightStore before atoms are accessed
 */
let serverBlockchain: BlockchainFilter = "ALL"
let serverDrops: SpotlightDrop[] = []
/**
 * By blockchain: the server renders several homepages at once in this
 * module, and one chain's launches must not show on another's.
 */
const serverLaunches = new Map<BlockchainFilter, LaunchedCollectionPublic[]>()
/** How launcher admins arranged each page's carousel, by page. */
export type CarouselLayouts = Partial<
  Record<string, readonly HomepageLayoutEntry[]>
>
/**
 * Every page's at once, from the latest render: switching the chain tab then
 * needs no request, and never draws the default order first.
 */
let serverLayouts: CarouselLayouts | undefined

/**
 * Bumped by the hydrator once a render brought a new server copy of the
 * launches and the layouts (a client navigation back to the same chain does):
 * the plain variables above cannot tell the store to read them again.
 */
export const serverLaunchesVersionAtom = atom(0, "spotlightServerLaunches")

/**
 * Initialize spotlight store with server data
 * Called from hydrator component on client mount
 */
export function initializeSpotlightStore(
  blockchain: BlockchainFilter,
  drops?: SpotlightDrop[],
  launches?: LaunchedCollectionPublic[],
  layouts?: CarouselLayouts,
) {
  serverBlockchain = blockchain
  if (drops) {
    serverDrops = drops
  }
  // A render that could not read them: never show an older render's copy
  if (launches) {
    serverLaunches.set(blockchain, launches)
  } else {
    serverLaunches.delete(blockchain)
  }
  // Like the launches: on the server this module outlives the request, and
  // the browser of a render that could not read them must draw what it drew
  serverLayouts = layouts
}

/**
 * Spotlight store molecule - single source of truth for drops spotlight.
 *
 * Architecture:
 * - Maintains own blockchain state (initialized from server via withInit)
 * - Fetches drops automatically when blockchain changes (withAsyncData)
 * - Enriches active slide with minted/eligibility data
 * - View syncs external blockchain changes → store's blockchain atom
 *
 * Data Flow:
 * Server → initializeSpotlightStore(blockchain) → Auto-fetch on blockchain change
 */
export const DropsSpotlightStoreMolecule = molecule((mol) => {
  const bffApiClient = mol(BffApiClientMolecule)
  const { activeSlideIndexAtom } = mol(SliderMolecule)
  const { unionAddress } = mol(WalletConnectMoleculeReatom)

  // Own blockchain state - initialized from server, synced from outside via setSelectedBlockchain
  const selectedBlockchainAtom = atom<BlockchainFilter>(
    "ALL",
    "selectedBlockchainAtom",
  ).extend(withInit(() => serverBlockchain))

  // Action to update blockchain (called by view when external state changes)
  const setSelectedBlockchain = action((blockchain: BlockchainFilter) => {
    if (blockchain !== selectedBlockchainAtom()) {
      selectedBlockchainAtom.set(blockchain)
    }
  }, "setSelectedBlockchain")

  // Async computed: Fetch drops when blockchain changes
  const dropsAtomAsync = computed(async () => {
    const blockchain = selectedBlockchainAtom()

    const response = await wrap(
      bffApiClient.api.drops.spotlight.$post({
        json: { blockchain },
      }),
    )

    if (!response.ok) throw new Error("Failed to fetch spotlight drops")

    const data = await wrap(response.json())

    // Filter out null values
    return data.filter(
      (drop): drop is NonNullable<typeof drop> => drop !== null,
    )
  }, "dropsAtomAsync").extend(withAsyncData())

  // Sync computed: Returns server data initially, then fetched data
  const dropsAtom = computed(() => {
    const data = dropsAtomAsync.data()
    const isPending = dropsAtomAsync.ready()
    const error = dropsAtomAsync.error()

    // If first render (no pending, no error, no data), use server data
    if (!isPending && !error && (!data || data.length === 0)) {
      return serverDrops
    }

    return data || []
  }, "dropsAtom")

  // Map of dropId -> enrichment data (minted counts, etc.)
  const enrichmentMapAtom = atom<Record<CollectionId, DropEnrichment>>(
    {},
    "enrichmentMapAtom",
  )

  // Get enrichment for a specific drop
  const getDropEnrichment = (dropId: CollectionId) =>
    computed(() => {
      const map = enrichmentMapAtom()
      return map[dropId]
    }, `getDropEnrichment-${dropId}`)

  // Check if a slide index is active
  const isSlideActive = (slideIndex: number) => {
    return activeSlideIndexAtom() === slideIndex
  }

  // Get active drop (just index lookup - cheap). Admins may put a drop
  // anywhere in the carousel, so it is looked up by the slide's position.
  const activeDropAtom = computed(() => {
    const slide = slideDataAtom()[activeSlideIndexAtom()]
    return slide?.type === "drop" ? slide.drop : undefined
  }, "activeDropAtom")

  // Get active drop's minted count from enrichment map
  const activeDropMintedCountAtom = computed(() => {
    const drop = activeDropAtom()
    if (!drop) return 0
    const enrichment = enrichmentMapAtom()[drop.id]
    return enrichment?.minted ?? 0
  }, "activeDropMintedCountAtom")

  // Get current phase for active drop (cheap calculation)
  const activeDropCurrentPhaseAtom = computed(() => {
    const drop = activeDropAtom()
    if (!drop) return null

    const now = Date.now()
    return (
      drop.phases.find((phase) => {
        const startDate = new Date(phase.startDate).getTime()
        const endDate = phase.endDate
          ? new Date(phase.endDate).getTime()
          : undefined
        return startDate <= now && (!endDate || now < endDate)
      }) || drop.phases[0]
    )
  }, "activeDropCurrentPhaseAtom")

  // Calculate fee for active drop (cheap calculation)
  const activeDropFeeAtom = computed(() => {
    const drop = activeDropAtom()
    const currentPhase = activeDropCurrentPhaseAtom()
    if (!drop || !currentPhase) return null

    const feesAmount = drop.fees.reduce((acc, fee) => {
      const feeValue =
        fee.type === "FLAT"
          ? fee.amount
          : fee.amount * Number(currentPhase.price.amount)
      return acc + feeValue
    }, 0)

    return {
      ...currentPhase.price,
      amount: feesAmount.toString(),
    }
  }, "activeDropFeeAtom")

  // Calculate display price for active drop (price without fees)
  const activeDropDisplayPriceAtom = computed(() => {
    const currentPhase = activeDropCurrentPhaseAtom()
    const fee = activeDropFeeAtom()
    if (!currentPhase || !fee) return null

    const MINIMUM_DISPLAY_PRICE = 0.0001
    const priceWithoutFees =
      Number(currentPhase.price.amount) - Number(fee.amount)
    const displayAmount =
      priceWithoutFees < MINIMUM_DISPLAY_PRICE
        ? "0"
        : priceWithoutFees.toString()

    return {
      ...currentPhase.price,
      amount: displayAmount,
    }
  }, "activeDropDisplayPriceAtom")

  // Check if active drop has ended (all phases expired or over supply)
  const activeDropHasEndedAtom = computed(() => {
    const drop = activeDropAtom()
    if (!drop) return false

    const now = Date.now()
    const minted = activeDropMintedCountAtom()

    // Check if over supply
    if (drop.totalSupply && minted >= drop.totalSupply) {
      return true
    }

    // Check if all phases have ended
    const allPhasesEnded = drop.phases.every((phase) => {
      if (!phase.endDate) return false
      return now >= new Date(phase.endDate).getTime()
    })

    return allPhasesEnded
  }, "activeDropHasEndedAtom")

  // Async computed: Automatically fetch enrichment when active drop changes
  const activeDropEnrichmentAtom = computed(async () => {
    const activeDrop = activeDropAtom()
    const address = unionAddress()

    if (!activeDrop?.id) return null

    try {
      const response = await wrap(
        bffApiClient.api.drops[":id"].minted.$post({
          param: { id: activeDrop.id },
          json: {
            id: activeDrop.id,
            owner: address,
          },
        }),
      )

      if (!response.ok) throw new Error("Failed to fetch minted data")

      const result = await wrap(response.json())

      // Update enrichment map
      enrichmentMapAtom.set({
        ...enrichmentMapAtom(),
        [activeDrop.id]: {
          minted: result.minted,
          myMints: result.myMints || 0,
        },
      })

      return {
        minted: result.minted,
        myMints: result.myMints || 0,
      }
    } catch (error) {
      console.error(`Failed to enrich drop ${activeDrop.id}:`, error)
      return null
    }
  }, "activeDropEnrichmentAtom").extend(withAsyncData())

  // Check if current phase is upcoming
  const isCurrentPhaseUpcomingAtom = computed(() => {
    const currentPhase = activeDropCurrentPhaseAtom()
    if (!currentPhase?.startDate) {
      return false
    }
    const now = Date.now()
    if (now > new Date(currentPhase.startDate).getTime()) {
      return false
    }
    return true
  }, "isCurrentPhaseUpcomingAtom")

  // Computed: Atomized active slide object
  const activeSlide = computed(() => {
    const drop = activeDropAtom()
    const enrichmentData = activeDropEnrichmentAtom.data()

    return {
      drop,
      minted: enrichmentData?.minted ?? 0,
      myMints: enrichmentData?.myMints ?? 0,
      currentPhase: activeDropCurrentPhaseAtom(),
      fee: activeDropFeeAtom(),
      displayPrice: activeDropDisplayPriceAtom(),
      hasEnded: activeDropHasEndedAtom(),
      isUpcomming: isCurrentPhaseUpcomingAtom(),
    }
  }, "activeSlide")

  // Collections published from the admin Collection Launcher, for the selected chain. A chain
  // the server rendered uses its copy (RSC navigation renders each chain afresh); only the
  // others are fetched.
  const launchesAtomAsync = computed(async () => {
    serverLaunchesVersionAtom()
    const blockchain = selectedBlockchainAtom()
    const rendered = serverLaunches.get(blockchain)
    if (rendered) return { blockchain, launches: rendered }

    const response = await wrap(
      bffApiClient.api["collection-launcher"].launches.$get({
        query: { blockchain },
      }),
    )

    if (!response.ok) throw new Error("Failed to fetch launched collections")

    return { blockchain, launches: await wrap(response.json()) }
  }, "launchesAtomAsync").extend(withAsyncData())

  // Never another chain's launches, even while a fetch for this one runs or after it failed
  const launchesAtom = computed(() => {
    serverLaunchesVersionAtom()
    const blockchain = selectedBlockchainAtom()
    const data = launchesAtomAsync.data()
    return data?.blockchain === blockchain
      ? data.launches
      : (serverLaunches.get(blockchain) ?? [])
  }, "launchesAtom")

  // How launcher admins arranged every page's carousel: the server's copy,
  // read here only when no render could
  const carouselLayoutsAtomAsync = computed(async () => {
    serverLaunchesVersionAtom()
    if (serverLayouts) return serverLayouts

    const response = await wrap(
      bffApiClient.api["collection-launcher"]["homepage-layout"].$get(),
    )

    if (!response.ok) throw new Error("Failed to fetch the homepage layouts")

    const layouts: CarouselLayouts = (await wrap(response.json())).carousel
    return layouts
  }, "carouselLayoutsAtomAsync").extend(withAsyncData())

  // This chain's; none (the default order) until the layouts are read
  const carouselLayoutAtom = computed(() => {
    serverLaunchesVersionAtom()
    const layouts = serverLayouts ?? carouselLayoutsAtomAsync.data()
    return layouts?.[selectedBlockchainAtom()]
  }, "carouselLayoutAtom")

  // Computed: Slide data, launched collections first, then brand slides and
  // drops, in the order launcher admins arranged and without the hidden ones
  const slideDataAtom = computed((): SlideData[] => {
    const slides = applyHomepageLayout(
      getDefaultCarouselSlides(
        selectedBlockchainAtom(),
        launchesAtom(),
        dropsAtom(),
      ),
      getCarouselSlideKey,
      carouselLayoutAtom(),
    )

    return slides.map((slide, slideIndex): SlideData => {
      if (slide.type === "brand") {
        return { type: "brand", slider: slide.slider, slideIndex }
      }
      return {
        type: "drop",
        drop: slide.drop,
        dropAtom: atomizeDrop(slide.drop, `spotlight-drop-${slide.drop.id}`),
        slideIndex,
      }
    })
  }, "slideDataAtom")

  // Computed: Final slides for Slider component (with JSX)
  const slidesAtom = computed(() => {
    const slideData = slideDataAtom()

    return slideData.map((data) => {
      if (data.type === "brand") {
        const { slider, slideIndex } = data

        return {
          title: <BrandSlideTitle slider={slider} />,
          name: slider.name,
          subtitle: null,
          preview: slider.image,
          previewType: isBrandSliderPreviewVideo(slider.image)
            ? DropPreviewType.Video
            : DropPreviewType.Image,
          previewPoster: slider.imagePoster,
          previewImages: slider.previewImages,
          background: slider.video.desktop,
          backgroundType: slider.backgroundType ?? ("video" as const),
          // Only the first slide is visible on load; preloading every slide
          // background competes with the LCP for bandwidth.
          imageWithPriority: slideIndex === 0,
        }
      }

      // Drop slide
      const { drop, dropAtom, slideIndex } = data
      const { id, title, preview, previewType, background, twitterLink } = drop

      return {
        title: (
          <DropScopeProvider dropId={id} dropAtom={dropAtom}>
            <DropTitle drop={drop} slideIndex={slideIndex} />
          </DropScopeProvider>
        ),
        name: title,
        subtitle: (
          <DropScopeProvider dropId={id} dropAtom={dropAtom}>
            <DropInfo drop={drop} slideIndex={slideIndex} />
          </DropScopeProvider>
        ),
        preview: preview ?? "",
        previewType: previewType ?? DropPreviewType.Image,
        background: background ?? "",
        twitterLink,
      }
    })
  }, "slidesAtom")

  return {
    // State
    selectedBlockchainAtom,
    dropsAtom,
    enrichmentMapAtom,

    // Actions
    setSelectedBlockchain,

    // Computed - Slides (main export)
    slidesAtom,

    // Computed - Active slide (atomized)
    activeSlide,

    // Utils
    isSlideActive,
  }
})
